import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Search, RefreshCw, Users2, Activity, Columns3 } from 'lucide-react';
import { useAdminAuth, API } from '../context/AdminAuthContext';
import AdminLayout from '../components/AdminLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { formatTime, formatDateTime } from '../lib/dateFormat';
import { useToast } from '../hooks/use-toast';

const KIND_OPTIONS = [
  { value: '__all__', label: 'All kinds' },
  { value: 'admin', label: 'Admin / Sub-admin' },
  { value: 'branch', label: 'Branch' },
  { value: 'customer', label: 'Customer' },
  { value: 'driver', label: 'Driver' },
];

// Report Access — table of every user account × 3 report toggles.
const ReportAccessSection = () => {
  const { authHeaders } = useAdminAuth();
  const [reports, setReports] = useState([]);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [q, setQ] = useState('');
  const [kind, setKind] = useState('__all__');
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState('');
  const LIMIT = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (kind && kind !== '__all__') params.set('kind', kind);
      params.set('limit', LIMIT);
      params.set('offset', offset);
      const { data } = await axios.get(
        `${API}/admin/access/users?${params.toString()}`,
        { headers: authHeaders },
      );
      setReports(data.reports || []);
      setUsers(data.users || []);
      setTotal(Number(data.total || 0));
    } catch (err) {
      console.warn('access users load failed', err);
    } finally {
      setLoading(false);
    }
  }, [authHeaders, q, kind, offset]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (u, rid, next) => {
    const key = `${u.kind}:${u.ref}:${rid}`;
    setSavingKey(key);
    // Optimistic local update.
    setUsers((prev) => prev.map((x) =>
      x.kind === u.kind && x.ref === u.ref
        ? { ...x, reports: { ...x.reports, [rid]: next } }
        : x,
    ));
    try {
      await axios.post(`${API}/admin/access/set`, {
        user_kind: u.kind,
        user_ref: u.ref,
        reports: { [rid]: next },
      }, { headers: authHeaders });
    } catch (err) {
      // Rollback on failure.
      setUsers((prev) => prev.map((x) =>
        x.kind === u.kind && x.ref === u.ref
          ? { ...x, reports: { ...x.reports, [rid]: !next } }
          : x,
      ));
      alert(err?.response?.data?.detail || 'Failed to update access');
    } finally {
      setSavingKey('');
    }
  };

  return (
    <section className="bg-white border border-neutral-200 rounded-2xl p-4 sm:p-6" data-testid="section-report-access">
      <header className="flex items-center gap-2 mb-3">
        <Users2 className="w-5 h-5 text-[#0078D4]" />
        <h2 className="text-lg font-bold text-[#0F172A]">Report Access</h2>
        <span className="text-xs text-neutral-500">— grant or revoke per user, per report</span>
      </header>

      <div className="flex flex-wrap gap-2 mb-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input
            data-testid="access-search-input"
            placeholder="Search by name, email or ID"
            value={q}
            onChange={(e) => { setOffset(0); setQ(e.target.value); }}
            className="pl-9"
          />
        </div>
        <Select value={kind} onValueChange={(v) => { setOffset(0); setKind(v); }}>
          <SelectTrigger className="w-[190px]" data-testid="access-kind-filter">
            <SelectValue placeholder="Filter by kind" />
          </SelectTrigger>
          <SelectContent>
            {KIND_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="access-refresh-btn">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      <div className="overflow-x-auto border border-neutral-200 rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-neutral-600 uppercase text-[11px] tracking-wider">
            <tr>
              <th className="px-3 py-2 text-left">User</th>
              <th className="px-3 py-2 text-left">Kind</th>
              <th className="px-3 py-2 text-left">Role</th>
              {reports.map((r) => (
                <th key={r.id} className="px-3 py-2 text-center">{r.label}</th>
              ))}
            </tr>
          </thead>
          <tbody data-testid="access-users-tbody">
            {users.length === 0 && !loading && (
              <tr><td colSpan={3 + reports.length} className="px-3 py-6 text-center text-neutral-400">No users match your filter.</td></tr>
            )}
            {users.map((u) => (
              <tr key={`${u.kind}:${u.ref}`} className="border-t border-neutral-100 hover:bg-neutral-50">
                <td className="px-3 py-2">
                  <div className="font-medium text-[#0F172A]">{u.label}</div>
                  <div className="text-xs text-neutral-500 truncate max-w-[280px]">{u.ref}</div>
                </td>
                <td className="px-3 py-2"><span className="inline-block px-2 py-0.5 rounded-full bg-neutral-100 text-xs uppercase tracking-wide text-neutral-600">{u.kind}</span></td>
                <td className="px-3 py-2 text-xs text-neutral-600">
                  <div>{u.role}</div>
                  {u.meta && <div className="text-neutral-400 truncate max-w-[200px]">{u.meta}</div>}
                </td>
                {reports.map((r) => {
                  const key = `${u.kind}:${u.ref}:${r.id}`;
                  const on = !!u.reports?.[r.id];
                  const isSuper = u.role === 'Super admin';
                  return (
                    <td key={r.id} className="px-3 py-2 text-center">
                      <Switch
                        checked={on}
                        disabled={isSuper || savingKey === key}
                        onCheckedChange={(v) => toggle(u, r.id, v)}
                        data-testid={`toggle-${u.kind}-${u.ref}-${r.id}`}
                        aria-label={`${on ? 'Revoke' : 'Grant'} ${r.label} for ${u.label}`}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-3 text-xs text-neutral-500">
        <div data-testid="access-pagination-summary">
          Showing {users.length ? offset + 1 : 0}–{offset + users.length} of {total}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={offset === 0 || loading}
            onClick={() => setOffset((o) => Math.max(0, o - LIMIT))}
            data-testid="access-prev-btn"
          >Prev</Button>
          <Button
            variant="outline"
            size="sm"
            disabled={offset + users.length >= total || loading}
            onClick={() => setOffset((o) => o + LIMIT)}
            data-testid="access-next-btn"
          >Next</Button>
        </div>
      </div>
    </section>
  );
};

// Warehouse feature toggles are now per-user — see AdminUsers.jsx's
// "Warehouse Access" section inside the create/edit modal. The old
// global controls section was removed on 2026-02-12.


// Branch Order Column Settings — super-admin toggles that hide/show
// individual columns on the Branch Orders page and the Branch New
// Order page for every branch user. Admin users never hit those
// pages (branch JWT only) so this section is a strict demote-only
// control — the "admins always see all columns" guarantee holds by
// construction. Persists in `system_settings.branch_columns.hidden`.
const BranchColumnSettingsSection = () => {
  const { authHeaders } = useAdminAuth();
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/branch-columns/catalog`, { headers: authHeaders });
      setColumns(data?.columns || []);
    } catch (err) {
      // silent — panel disables itself until the next reload succeeds.
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  // Toggle a single column. Optimistic — flip the UI first, roll back
  // if the server rejects (403 for non-super admin).
  const toggle = async (cid, hidden) => {
    setSavingId(cid);
    setColumns((prev) => prev.map((c) => c.id === cid ? { ...c, hidden } : c));
    try {
      await axios.post(`${API}/admin/branch-columns`,
        { column_id: cid, hidden }, { headers: authHeaders });
    } catch (err) {
      setColumns((prev) => prev.map((c) => c.id === cid ? { ...c, hidden: !hidden } : c));
      alert(err?.response?.data?.detail || 'Failed to update column');
    } finally {
      setSavingId('');
    }
  };

  // Group columns by page so the admin scans a shorter list per section.
  const grouped = columns.reduce((acc, c) => {
    const bucket = c.id.startsWith('orders.') ? 'Branch Orders' : 'Branch New Order';
    (acc[bucket] ||= []).push(c);
    return acc;
  }, {});

  return (
    <section
      className="bg-white border border-neutral-200 rounded-2xl p-4 sm:p-6"
      data-testid="section-branch-column-settings"
    >
      <header className="flex items-center gap-2 mb-3">
        <Columns3 className="w-5 h-5 text-[#0078D4]" />
        <h2 className="text-lg font-bold text-[#0F172A]">Branch Order Column Settings</h2>
        <span className="text-xs text-neutral-500">— hide individual columns from branch users</span>
      </header>
      {loading ? (
        <div className="text-sm text-neutral-400 py-4">Loading columns…</div>
      ) : (
        <div className="space-y-5">
          {Object.entries(grouped).map(([bucket, list]) => (
            <div key={bucket}>
              <div className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-2">{bucket}</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {list.map((c) => {
                  const enabled = !c.hidden;
                  return (
                    <div
                      key={c.id}
                      className="flex items-center gap-3 rounded-xl border border-neutral-200 bg-neutral-50/70 px-3 py-2.5"
                      data-testid={`branch-col-row-${c.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-[#0F172A] truncate">
                          {c.label.replace(/^Branch Orders — |^New Order — /, '')}
                        </div>
                        <div className="text-[11px] text-neutral-500">Column id · <code>{c.id}</code></div>
                      </div>
                      <span
                        className="text-xs text-neutral-500 tabular-nums"
                        data-testid={`branch-col-state-${c.id}`}
                      >
                        {enabled ? 'ON' : 'OFF'}
                      </span>
                      <Switch
                        checked={enabled}
                        disabled={savingId === c.id}
                        onCheckedChange={(v) => toggle(c.id, !v)}
                        data-testid={`branch-col-toggle-${c.id}`}
                        aria-label={`${enabled ? 'Hide' : 'Show'} ${c.label}`}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
      <p className="text-[11px] text-neutral-400 mt-4">
        Admin users always see every column regardless of these toggles. Changes take effect on the branch user&rsquo;s next page load or window focus.
      </p>
    </section>
  );
};


// Live Visitors — polls every 30s. Auto-removes stale rows because
// the backend filter already only returns sessions from the last 5 min.
const GoogleDriveSection = () => {
  // Feb 2026 storage swap — one-time OAuth consent that captures a
  // refresh token so backend uploads (product images, order docs,
  // stock receipts) land in the operator's personal Drive folder.
  // Only the super admin sees this section (the parent already gates
  // the whole page on `isSuper`).
  const { toast } = useToast();
  const { authHeaders } = useAdminAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/gdrive/status`, { headers: authHeaders });
      setStatus(data);
    } catch (e) {
      setStatus({ connected: false, oauth_configured: false });
    } finally { setLoading(false); }
  }, [authHeaders]);

  useEffect(() => {
    load();
    // Handle callback query params so the toast fires immediately on
    // return from Google's consent screen.
    const p = new URLSearchParams(window.location.search);
    if (p.get('gdrive') === 'connected') {
      toast({ title: 'Google Drive connected', description: 'Uploads will now land in your Drive folder.' });
      const url = new URL(window.location.href);
      url.searchParams.delete('gdrive');
      window.history.replaceState({}, '', url.toString());
    } else if (p.get('gdrive') === 'error') {
      toast({ title: 'Google Drive connection failed', description: p.get('reason') || 'Try again.' });
    }
  }, [load, toast]);

  const startConnect = async () => {
    setConnecting(true);
    try {
      const { data } = await axios.get(`${API}/admin/gdrive/connect`, { headers: authHeaders });
      if (data?.auth_url) {
        window.location.href = data.auth_url;
      } else {
        toast({ title: 'No auth URL returned' });
      }
    } catch (e) {
      toast({ title: 'Could not start OAuth', description: e.response?.data?.detail || 'Try again' });
    } finally { setConnecting(false); }
  };

  const connected = !!status?.connected;
  return (
    <section className="bg-white border border-neutral-200 rounded-2xl p-4 sm:p-6" data-testid="section-gdrive">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <h2 className="text-lg font-bold text-[#0F172A]">Google Drive storage</h2>
          <p className="text-xs text-neutral-500 mt-1 max-w-lg leading-relaxed">
            Every user-uploaded file (product images, order documents, goods photos, stock receipts) is written to your personal Google Drive folder. Connect once and the app keeps a refresh token so future uploads run headless.
          </p>
        </div>
        <span
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${
            connected ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      : 'bg-neutral-100 text-neutral-600 border border-neutral-200'
          }`}
          data-testid="gdrive-status-badge"
        >
          <span className={`w-1.5 h-1.5 rounded-full ${connected ? 'bg-emerald-500 animate-pulse' : 'bg-neutral-400'}`} />
          {connected ? 'Connected' : 'Not connected'}
        </span>
      </div>
      {loading ? (
        <div className="text-xs text-neutral-400">Loading…</div>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          {connected ? (
            <>
              <div className="text-xs text-neutral-500">
                Folder ID: <code className="font-mono text-[11px] text-neutral-700">{status.folder_id || '—'}</code>
              </div>
              <button
                type="button"
                onClick={startConnect}
                disabled={connecting}
                data-testid="gdrive-reconnect-btn"
                className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border border-neutral-300 hover:bg-neutral-50 text-neutral-700 disabled:opacity-50"
              >
                {connecting ? 'Redirecting…' : 'Reconnect / switch account'}
              </button>
            </>
          ) : (
            <>
              {!status?.oauth_configured && (
                <div className="text-xs text-red-600 flex-1">
                  OAuth env vars are missing on the backend. Set <code>GOOGLE_OAUTH_CLIENT_ID</code>, <code>GOOGLE_OAUTH_CLIENT_SECRET</code>, and <code>GOOGLE_DRIVE_FOLDER_ID</code>, then reload.
                </div>
              )}
              <button
                type="button"
                onClick={startConnect}
                disabled={connecting || !status?.oauth_configured}
                data-testid="gdrive-connect-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-[#0078D4] hover:bg-[#005A9E] text-white disabled:opacity-50"
              >
                {connecting ? 'Redirecting…' : 'Connect Google Drive'}
              </button>
            </>
          )}
        </div>
      )}
    </section>
  );
};



const LiveVisitorsSection = () => {
  const { authHeaders } = useAdminAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastFetched, setLastFetched] = useState(null);
  const [blockAnon, setBlockAnon] = useState(null);
  const [blockSaving, setBlockSaving] = useState(false);
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/activity/live`, { headers: authHeaders });
      setRows(data.visitors || []);
      setLastFetched(new Date());
    } catch (err) {
      // Silent — network / auth blip. Retry on next tick.
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  // Fetch the current toggle state once on mount.
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/admin/access/anonymous-block`, { headers: authHeaders })
      .then(({ data }) => { if (alive) setBlockAnon(!!data?.enabled); })
      .catch(() => { if (alive) setBlockAnon(true); });
    return () => { alive = false; };
  }, [authHeaders]);

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, 30_000);
    return () => clearInterval(timerRef.current);
  }, [load]);

  const toggleBlockAnon = async (next) => {
    setBlockSaving(true);
    const prev = blockAnon;
    setBlockAnon(next); // optimistic
    try {
      await axios.post(`${API}/admin/access/anonymous-block`,
        { enabled: next }, { headers: authHeaders });
    } catch (err) {
      setBlockAnon(prev);
      alert(err?.response?.data?.detail || 'Failed to save toggle');
    } finally {
      setBlockSaving(false);
    }
  };

  const fmtOpened = (iso) => formatTime(iso) || '—';
  const fmtAgo = (iso) => {
    if (!iso) return '—';
    const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
    if (s < 60) return `${s}s ago`;
    if (s < 300) return `${Math.floor(s / 60)}m ago`;
    return `${Math.floor(s / 60)}m ago`;
  };

  return (
    <section className="bg-white border border-neutral-200 rounded-2xl p-4 sm:p-6" data-testid="section-live-visitors">
      <header className="flex items-center gap-2 mb-3">
        <Activity className="w-5 h-5 text-emerald-600" />
        <h2 className="text-lg font-bold text-[#0F172A]">Live Visitors</h2>
        <span className="text-xs text-neutral-500">— active in the last 5 min · updates every 30 s</span>
        <div className="ml-auto flex items-center gap-2 text-xs text-neutral-400">
          <span data-testid="visitors-count">{rows.length} active</span>
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
        </div>
      </header>

      {/* Block Anonymous Visitors — system-wide toggle */}
      <div
        className="flex items-center gap-3 mb-4 rounded-xl border border-neutral-200 bg-neutral-50/70 px-3 py-2.5"
        data-testid="block-anon-toggle-row"
      >
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-[#0F172A]">Block Anonymous Visitors</div>
          <div className="text-xs text-neutral-500 leading-snug">
            When ON, unauthenticated visitors to any public report share link
            or the Branch Orders portal are redirected to the login page.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-neutral-500" data-testid="block-anon-state-label">
            {blockAnon === null ? 'Loading…' : (blockAnon ? 'ON' : 'OFF')}
          </span>
          <Switch
            checked={!!blockAnon}
            disabled={blockAnon === null || blockSaving}
            onCheckedChange={toggleBlockAnon}
            data-testid="block-anon-toggle"
            aria-label="Block anonymous visitors"
          />
        </div>
      </div>

      <div className="overflow-x-auto border border-neutral-200 rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-neutral-600 uppercase text-[11px] tracking-wider">
            <tr>
              <th className="px-3 py-2 text-left">User</th>
              <th className="px-3 py-2 text-left">Kind</th>
              <th className="px-3 py-2 text-left">Report</th>
              <th className="px-3 py-2 text-left">Page</th>
              <th className="px-3 py-2 text-left">Opened</th>
              <th className="px-3 py-2 text-left">Last seen</th>
            </tr>
          </thead>
          <tbody data-testid="visitors-tbody">
            {rows.length === 0 && !loading && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-neutral-400">No active visitors right now.</td></tr>
            )}
            {rows.map((v) => {
              const reportLabel = v.report || 'Unknown';
              const isUnknown = reportLabel === 'Unknown';
              return (
                <tr key={v.session_id} className="border-t border-neutral-100 hover:bg-neutral-50">
                  <td className="px-3 py-2">
                    <div className="font-medium text-[#0F172A]">{v.user_label}</div>
                    {v.user_ref && <div className="text-xs text-neutral-500 truncate max-w-[240px]">{v.user_ref}</div>}
                  </td>
                  <td className="px-3 py-2"><span className="inline-block px-2 py-0.5 rounded-full bg-neutral-100 text-xs uppercase tracking-wide text-neutral-600">{v.user_kind}</span></td>
                  <td className="px-3 py-2" data-testid={`visitor-report-${v.session_id}`}>
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                      isUnknown ? 'bg-neutral-100 text-neutral-500' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                    }`}>
                      {reportLabel}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-neutral-700 truncate max-w-[320px]">{v.page || '—'}</td>
                  <td className="px-3 py-2 text-xs text-neutral-600">{fmtOpened(v.opened_at)}</td>
                  <td className="px-3 py-2 text-xs text-neutral-600">{fmtAgo(v.last_seen_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {lastFetched && (
        <div className="mt-2 text-[11px] text-neutral-400" data-testid="visitors-last-fetched">
          Last refreshed at {formatTime(lastFetched)}
        </div>
      )}
    </section>
  );
};

const AccessActivity = () => {
  const { authHeaders } = useAdminAuth();
  const [me, setMe] = useState(null);
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/admin/me`, { headers: authHeaders })
      .then(({ data }) => { if (alive) setMe(data); })
      .catch(() => { if (alive) setMe({ is_super_admin: false }); });
    return () => { alive = false; };
  }, [authHeaders]);
  const isSuper = !!me?.is_super_admin;
  if (me && !isSuper) {
    return (
      <AdminLayout>
        <div className="p-6" data-testid="access-activity-forbidden">
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
            Access &amp; Activity is visible to the super admin only.
          </div>
        </div>
      </AdminLayout>
    );
  }
  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 space-y-6" data-testid="access-activity-root">
        <header>
          <h1 className="text-2xl font-extrabold text-[#0F172A] font-display">Access &amp; Activity</h1>
          <p className="text-sm text-neutral-500 mt-1">
            Grant per-user access to reports and watch who is active in the app right now.
          </p>
        </header>
        <ReportAccessSection />
        <BranchColumnSettingsSection />
        <GoogleDriveSection />
        <LiveVisitorsSection />
      </div>
    </AdminLayout>
  );
};

export default AccessActivity;
